import { CleanupFunction, DecisionContext } from "@/dbot/common/commonDomain";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import { CompanyRole } from "@/types/CompanyInfo";
import {
  extractBoardMembers,
  extractChairperson,
  extractDeputyChairpersonName,
  validateName,
} from "@/utils/Roles";
import { compact, identity } from "lodash";

import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { BooleanFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";

export const NAMED_PERSON = "namedPerson";

export type EmploymentOfManagingDirector = {
  type: DecisionType.employmentOfManagingDirector;

  newManagingDirector?: string;
  createManagingDirectorEmploymentAgreement?: boolean;
  receivedManagingDirectorEmploymentAgreement?: string; // "changed", "true" or "false"
  showManagingDirectorEmploymentAgreementChanges?: boolean;
  managingDirectorEmploymentAgreementChanges?: string;
  giveProcuraManagingDirectorEmploymentAgreementSigning?: boolean;
  newProcuraManagingDirectorEmploymentAgreementSigning?: string;
  newProcuraManagingDirectorEmploymentAgreementSigningNamedPerson?: string;
  procuraManagingDirectorEmploymentAgreementNegotiation?: boolean;
  newProcuraManagingDirectorEmploymentAgreementNegotiation?: string; // "namedPerson"
  newProcuraManagingDirectorEmploymentAgreementNegotiationNamedPerson?: string;
  giveProcuraManagingDirectorEmploymentAgreementSigningSame?: boolean;
};

export class EmploymentOfManagingDirectorForm {
  readonly type = DecisionType.employmentOfManagingDirector;

  newManagingDirector: StringFieldModel;
  createManagingDirectorEmploymentAgreement: BooleanFieldModel;
  receivedManagingDirectorEmploymentAgreement: RadioFieldModel<string | undefined>; // "changed", "true" or "false"
  showManagingDirectorEmploymentAgreementChanges: BooleanFieldModel;
  managingDirectorEmploymentAgreementChanges: StringFieldModel;
  giveProcuraManagingDirectorEmploymentAgreementSigning: BooleanFieldModel;
  newProcuraManagingDirectorEmploymentAgreementSigning: RadioFieldModel<string | undefined>;
  newProcuraManagingDirectorEmploymentAgreementSigningNamedPerson: StringFieldModel;
  procuraManagingDirectorEmploymentAgreementNegotiation: BooleanFieldModel;
  newProcuraManagingDirectorEmploymentAgreementNegotiation: RadioFieldModel<string | undefined>; // "namedPerson"
  newProcuraManagingDirectorEmploymentAgreementNegotiationNamedPerson: StringFieldModel;
  giveProcuraManagingDirectorEmploymentAgreementSigningSame: BooleanFieldModel;

  constructor(
    readonly context: DecisionContext<EmploymentOfManagingDirector>,
    readonly data?: EmploymentOfManagingDirector,
  ) {
    this.newManagingDirector = new StringFieldModel({
      value: data?.newManagingDirector,
      required: true,
      validator: validateName,
    });
    this.createManagingDirectorEmploymentAgreement = new BooleanFieldModel({
      value: data?.createManagingDirectorEmploymentAgreement,
      required: true,
    });
    this.receivedManagingDirectorEmploymentAgreement = new RadioFieldModel({
      value: data?.receivedManagingDirectorEmploymentAgreement,
      options: ["true", "changed", "false"],
      idFn: identity,
      labelFn: (v) => {
        switch (v) {
          case "true":
            return {
              key: `decisions.employmentOfManagingDirector.receivedEmploymentAgreement-true`,
            };
          case "false":
            return {
              key: `decisions.employmentOfManagingDirector.receivedEmploymentAgreement-false`,
            };
          case "changed":
            return {
              key: `decisions.employmentOfManagingDirector.receivedEmploymentAgreement-changed`,
            };
          default:
            return { translated: "" };
        }
      },
    });
    this.showManagingDirectorEmploymentAgreementChanges = new BooleanFieldModel({
      value: data?.showManagingDirectorEmploymentAgreementChanges,
    });
    this.managingDirectorEmploymentAgreementChanges = new StringFieldModel({
      value: data?.managingDirectorEmploymentAgreementChanges,
    });
    this.giveProcuraManagingDirectorEmploymentAgreementSigning = new BooleanFieldModel({
      value: data?.giveProcuraManagingDirectorEmploymentAgreementSigning,
    });
    this.newProcuraManagingDirectorEmploymentAgreementSigning = new RadioFieldModel({
      value:
        data?.newProcuraManagingDirectorEmploymentAgreementSigning ??
        extractChairperson(this.roles)?.name,
      options: this.boardMembers,
      idFn: identity,
      labelFn: this.boardMemberLabel,
    });
    this.newProcuraManagingDirectorEmploymentAgreementSigningNamedPerson = new StringFieldModel({
      value: data?.newProcuraManagingDirectorEmploymentAgreementSigningNamedPerson,
    });
    this.procuraManagingDirectorEmploymentAgreementNegotiation = new BooleanFieldModel({
      value: data?.procuraManagingDirectorEmploymentAgreementNegotiation,
    });
    this.newProcuraManagingDirectorEmploymentAgreementNegotiation = new RadioFieldModel({
      value:
        data?.newProcuraManagingDirectorEmploymentAgreementNegotiation ??
        extractChairperson(this.roles)?.name,
      options: this.boardMembers,
      idFn: identity,
      labelFn: this.boardMemberLabel,
    });
    this.newProcuraManagingDirectorEmploymentAgreementNegotiationNamedPerson = new StringFieldModel(
      { value: data?.newProcuraManagingDirectorEmploymentAgreementNegotiationNamedPerson },
    );
    this.giveProcuraManagingDirectorEmploymentAgreementSigningSame = new BooleanFieldModel({
      value: data?.giveProcuraManagingDirectorEmploymentAgreementSigningSame ?? true,
    });
    makeAutoObservable(this);
  }

  init(): CleanupFunction {
    return reaction(
      () => this.context.isBatchProject,
      (isBatchProject) =>
        runInAction(() => {
          if (isBatchProject) {
            this.procuraManagingDirectorEmploymentAgreementNegotiation.setValue(false);
            this.giveProcuraManagingDirectorEmploymentAgreementSigning.setValue(false);
          }
        }),
      { fireImmediately: true },
    );
  }

  get boardMembers(): string[] {
    return compact([
      extractChairperson(this.roles)?.name || "",
      extractDeputyChairpersonName(this.roles),
      ...extractBoardMembers(this.roles),
      NAMED_PERSON,
    ]);
  }

  boardMemberLabel(v: string | undefined): TMessage {
    if (v === NAMED_PERSON) return { key: "decisions.employmentOfManagingDirector.namedPerson" };
    return { translated: v ?? "" };
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return (
      this.newManagingDirector.isValid &&
      this.createManagingDirectorEmploymentAgreement.isValid &&
      this.showManagingDirectorEmploymentAgreementChanges.isValid &&
      this.managingDirectorEmploymentAgreementChanges.isValid &&
      this.giveProcuraManagingDirectorEmploymentAgreementSigning.isValid &&
      this.newProcuraManagingDirectorEmploymentAgreementSigningNamedPerson.isValid &&
      this.procuraManagingDirectorEmploymentAgreementNegotiation.isValid &&
      this.newProcuraManagingDirectorEmploymentAgreementNegotiationNamedPerson.isValid &&
      this.giveProcuraManagingDirectorEmploymentAgreementSigningSame.isValid
    );
  }

  validate(): boolean {
    this.newManagingDirector.validate();
    this.createManagingDirectorEmploymentAgreement.validate();
    this.showManagingDirectorEmploymentAgreementChanges.validate();
    this.managingDirectorEmploymentAgreementChanges.validate();
    this.giveProcuraManagingDirectorEmploymentAgreementSigning.validate();
    this.newProcuraManagingDirectorEmploymentAgreementSigningNamedPerson.validate();
    this.procuraManagingDirectorEmploymentAgreementNegotiation.validate();
    this.newProcuraManagingDirectorEmploymentAgreementNegotiationNamedPerson.validate();
    this.giveProcuraManagingDirectorEmploymentAgreementSigningSame.validate();
    return this.isValid;
  }

  get showShowEmploymentAgreementChanges(): boolean {
    return (
      this.createManagingDirectorEmploymentAgreement.value === true &&
      this.receivedManagingDirectorEmploymentAgreement.value === "changed"
    );
  }

  get showGiveProcuraRights(): boolean {
    return (
      this.createManagingDirectorEmploymentAgreement.value === true &&
      (((this.receivedManagingDirectorEmploymentAgreement.value === "true" ||
        this.receivedManagingDirectorEmploymentAgreement.value === "changed") &&
        this.giveProcuraManagingDirectorEmploymentAgreementSigning.value) ||
        (this.receivedManagingDirectorEmploymentAgreement.value === "false" &&
          this.procuraManagingDirectorEmploymentAgreementNegotiation.value === true &&
          this.newProcuraManagingDirectorEmploymentAgreementNegotiation.value !== undefined &&
          this.giveProcuraManagingDirectorEmploymentAgreementSigningSame.value === false))
    );
  }

  get roles(): CompanyRole[] {
    return this.context.initialState.roles;
  }
}
